/**
 * Resources screen (DNKADM-0054 U1) — the `v2_resource` entity list + create/edit/
 * deactivate/reactivate (handoff §5.7). `RequireAdmin`-gated route + a Scheduling nav
 * item ("Resources"). Replaces the v1 Any-Bay / ServiceAccount machinery with a
 * first-class resource entity carrying **capacity** (§9.1/§9.2).
 *
 * Loads `listResources({ status })` through `useResource`, keyed on the
 * Active/Inactive/All filter (`useActiveFilter` + `<ActiveFilter>`, §4.3). A table of
 * name → capacity → status; the row/name/chevron click opens the detail (schedule,
 * exceptions) per the DNKADM-0284 row-open pattern, with Deactivate/Reactivate as
 * explicit secondary actions that stop propagation. Base-field editing lives on the
 * detail page (DNKADM-0286 retired the list quick-edit trigger); "Add resource"
 * opens the {@link ResourceDialog} to create. Every write refetches + toasts. A
 * first-run empty tenant (§8) shows a strong empty state with a create CTA.
 *
 * **Toolbar, sort and URL (DNKADM-0430 P1-F).** The shared {@link ListToolbar} holds a
 * name search and the Active/Inactive/All filter; Name, Capacity and Status have shared
 * {@link SortableHeader}s (default Name ascending). Search, filter and sort live in the
 * URL (`resourcesListView.ts`).
 */
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Link as MuiLink,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { ListNoMatch } from '@/components/list/ListNoMatch';
import { ListToolbar } from '@/components/list/ListToolbar';
import { SortableHeader } from '@/components/list/SortableHeader';
import { parseSort, sortRows, toggleSort, toSortParams } from '@/components/list/listSort';
import { useListUrlState } from '@/components/list/useListUrlState';
import { Icon, resolveTokens } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useUrlActiveFilter } from '@/hooks/useActiveFilter';
import { ActiveFilter } from '@/components/ActiveFilter';
import { AssignedLocationsCell } from '@/components/AssignedLocationsCell';
import { useToast } from '@/components/useToast';
import { serverMessage } from '@/api/serverMessage';
import {
  deactivateResource,
  listResources,
  reactivateResource,
  type V2Resource,
} from '@/api/resources';
import { getPlanUsage, planLimitFor } from '@/api/planUsage';
import { PlanLimitDialog } from '@/components/PlanLimitDialog';
import { parsePlanLimit, v2ErrorCode, type PlanLimitInfo } from '@/api/v2Errors';
import { ResourceDialog } from './ResourceDialog';
import {
  RESOURCE_DEFAULT_SORT,
  RESOURCE_SORT_KEYS,
  RESOURCES_URL_DEFAULTS,
  resourceMatches,
  resourceName,
  resourceSortValue,
  type ResourceSortKey,
} from './resourcesListView';
import { ScreenError } from '@/components/ScreenError';

export function ResourcesScreen() {
  const t = resolveTokens(useTheme());
  const showToast = useToast();
  const navigate = useNavigate();
  // DNKADM-0430: search, status filter and sort come from the URL (`resourcesListView.ts`).
  const url = useListUrlState(RESOURCES_URL_DEFAULTS);
  const setUrl = url.set;
  const filter = useUrlActiveFilter(url.values.status, setUrl);
  const search = url.values.q;
  const sort = parseSort(url.values.sort, url.values.dir, RESOURCE_SORT_KEYS, RESOURCE_DEFAULT_SORT);

  const resources = useResource(
    useCallback(() => listResources({ status: filter.status }), [filter.status]),
    [filter.status],
  );

  // Plan usage drives the button-level Add gate (DNKADM-0069); loaded once and
  // refetched after each add/delete (a slot changes). Degrades to the submit-time
  // gate (DNKADM-0065) when it can't load — `planLimitFor` returns null on error.
  const planUsage = useResource(getPlanUsage);

  // Dialog state: `null` = closed; `{ resource: null }` = add; `{ resource }` = edit.
  const [dialog, setDialog] = useState<{ resource: V2Resource | null } | null>(null);
  // The plan-limit popup payload when Add is gated at the button; null = closed.
  const [planLimit, setPlanLimit] = useState<PlanLimitInfo | null>(null);

  const refetch = resources.refetch;

  const visibleRows = useMemo(
    () =>
      sortRows(
        (resources.data ?? []).filter((row) => resourceMatches(row, search)),
        (row) => resourceSortValue(row, sort.key),
        sort.dir,
        (row) => row.id,
      ),
    [resources.data, search, sort.key, sort.dir],
  );
  const onSort = useCallback(
    (key: ResourceSortKey) => setUrl(toSortParams(toggleSort(sort, key), RESOURCE_DEFAULT_SORT)),
    [setUrl, sort],
  );
  const refetchUsage = planUsage.refetch;

  // Add: when the axis is at its plan cap, open the upgrade popup directly (the
  // form never opens); otherwise open the create form exactly as before. An
  // unknown/unlimited/unavailable usage never gates (`planLimitFor` → null).
  const handleAdd = useCallback(() => {
    const info = planLimitFor(planUsage.data, 'resources');
    if (info) {
      setPlanLimit(info);
      return;
    }
    setDialog({ resource: null });
  }, [planUsage.data]);

  const handleDeactivate = useCallback(
    async (resource: V2Resource) => {
      try {
        await deactivateResource(resource.id);
        refetch();
        refetchUsage();
        showToast('Resource deactivated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t deactivate the resource.', 'error');
      }
    },
    [refetch, refetchUsage, showToast],
  );

  const handleReactivate = useCallback(
    async (resource: V2Resource) => {
      try {
        await reactivateResource(resource.id);
        refetch();
        refetchUsage();
        showToast('Resource reactivated', 'success');
      } catch (err) {
        // Reactivate consumes a plan slot (counts are of ACTIVE records), so it is
        // plan-limit enforced exactly like Add (DNKADM-0065 / DNKAPI-0139) — mirror
        // the create flow's branching, keyed on the body `error` code (not status).
        const code = v2ErrorCode(err);
        if (code === 'PLAN_LIMIT_REACHED') {
          // The cap was hit — open the upgrade popup (NOT a generic toast); the
          // record was not reactivated.
          setPlanLimit(parsePlanLimit(err));
          return;
        }
        if (code === 'PLAN_VERIFY_FAILED') {
          // Transient plan-service outage — a retry toast (NOT the popup).
          showToast(
            'We couldn’t verify your plan just now. Please try again in a moment.',
            'error',
          );
          return;
        }
        showToast(serverMessage(err) ?? 'Couldn’t reactivate the resource.', 'error');
      }
    },
    [refetch, refetchUsage, showToast],
  );

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          mb: 2,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" component="h1">
            Resources
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
            The bookable resources (courts, bays, rooms) services run on. Set each
            resource’s capacity, assign it to locations, then author its weekly schedule
            and date exceptions.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Icon name="add" />}
          onClick={handleAdd}
          sx={{ flexShrink: 0 }}
        >
          Add resource
        </Button>
      </Stack>

      <ListToolbar
        search={{
          value: search,
          onChange: (next) => setUrl({ q: next }),
          placeholder: 'Search resources',
          disabled: resources.status !== 'ready',
        }}
        filters={
          <ActiveFilter
            value={filter.value}
            onChange={filter.setValue}
            aria-label="Filter resources by status"
          />
        }
        onClear={() => setUrl({ q: null, status: null })}
        canClear={search.length > 0 || filter.value !== 'active'}
      />

      {resources.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={220}
          role="status"
          aria-label="Loading resources"
          sx={{ borderRadius: 3 }}
        />
      )}

      {resources.status === 'error' && (
        <ScreenError
          error={resources.error}
          what="the resources"
          onRetry={resources.refetch}
          sx={{ alignItems: 'center' }}
        />
      )}

      {resources.status === 'ready' && resources.data && resources.data.length === 0 && (
        // First-run empty state (§8) with a strong create CTA.
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: t.lineStrong,
            borderRadius: 3,
          }}
        >
          <Icon name="meeting_room" sx={{ fontSize: 40, color: 'text.disabled' }} />
          <Typography sx={{ mt: 1, fontWeight: 600 }}>
            {filter.value === 'active' ? 'No resources yet' : 'No resources match this filter'}
          </Typography>
          {filter.value === 'active' && (
            <>
              <Typography variant="body2" sx={{ mt: 0.5, mb: 2.5, color: 'text.secondary' }}>
                Add your first resource, set its capacity, assign it to locations, and set
                its schedule to start offering bookable services.
              </Typography>
              <Button variant="contained" startIcon={<Icon name="add" />} onClick={handleAdd}>
                Add resource
              </Button>
            </>
          )}
        </Paper>
      )}

      {resources.status === 'ready' && resources.data && resources.data.length > 0 && (
        <TableContainer>
          <Table aria-label="Resources" size="small">
            <TableHead>
              <TableRow>
                <SortableHeader label="Name" col="name" sort={sort} onSort={onSort} />
                <SortableHeader label="Capacity" col="capacity" sort={sort} onSort={onSort} />
                <TableCell>Locations</TableCell>
                <SortableHeader label="Status" col="status" sort={sort} onSort={onSort} />
                <TableCell align="right">Actions</TableCell>
                <TableCell sx={{ width: 44 }} aria-hidden />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((resource) => {
                const name = resourceName(resource);
                return (
                  <TableRow
                    key={resource.id}
                    hover
                    onClick={() => navigate(`/resources/${resource.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <MuiLink
                        component={RouterLink}
                        to={`/resources/${resource.id}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {name}
                      </MuiLink>
                    </TableCell>
                    <TableCell>{resource.capacity}</TableCell>
                    <TableCell>
                      <AssignedLocationsCell locations={resource.locations} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={resource.isActive ? 'Active' : 'Inactive'}
                        color={resource.isActive ? 'success' : 'default'}
                        variant={resource.isActive ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {resource.isActive ? (
                        <IconAction
                          size="small"
                          label={`Deactivate resource ${name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleDeactivate(resource);
                          }}
                        >
                          <Icon name="toggle_off" />
                        </IconAction>
                      ) : (
                        <IconAction
                          size="small"
                          label={`Reactivate resource ${name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleReactivate(resource);
                          }}
                        >
                          <Icon name="toggle_on" />
                        </IconAction>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ color: t.textGhost }}>
                      <IconAction
                        size="small"
                        label={`Open ${name}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/resources/${resource.id}`);
                        }}
                      >
                        <Icon name="chevron_right" />
                      </IconAction>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {resources.status === 'ready' &&
        resources.data &&
        resources.data.length > 0 &&
        visibleRows.length === 0 && <ListNoMatch noun="resources" query={search} />}

      <ResourceDialog
        open={dialog !== null}
        resource={dialog?.resource ?? null}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          refetch();
          refetchUsage();
        }}
        showToast={showToast}
      />

      <PlanLimitDialog
        open={planLimit !== null}
        info={planLimit}
        onClose={() => setPlanLimit(null)}
      />
    </Box>
  );
}
